import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {BaseProgressToolCall, DeepResearchToolType} from 'llm/deep-research/tool-details/types';
import {FONT_PROXIMA_12, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';

export interface BaseToolDetailProps {
  toolCall: BaseProgressToolCall;
}

/**
 * Extracts input parameters from a tool call for display
 */
function getToolInputString(toolCall: BaseProgressToolCall): string {
  // Use type assertion with specific tool call interface
  const toolCallWithProps = toolCall as BaseProgressToolCall & {
    query?: string;
    objectTypes?: Array<string>;
    notebookId?: string;
    attachmentId?: string;
    objectIds?: Array<string>;
    includeRelevantItems?: boolean;
  };

  switch (toolCall.tool) {
    case DeepResearchToolType.SearchTool:
      return `Query: "${toolCallWithProps.query || ''}"${toolCallWithProps.objectTypes?.length ? `\nObject Types: ${toolCallWithProps.objectTypes.join(', ')}` : ''}`;
    case DeepResearchToolType.SearchNotebookEntries:
      return `Query: "${toolCallWithProps.query || ''}"`;
    case DeepResearchToolType.SearchSequencesTool:
      return `Query: "${toolCallWithProps.query || ''}"`;
    case DeepResearchToolType.QueryWarehouseTool:
      return `Query: "${toolCallWithProps.query || ''}"`;
    case DeepResearchToolType.ReadBenchlingDocumentTool:
      return `Notebook ID: ${toolCallWithProps.notebookId || 'N/A'}`;
    case DeepResearchToolType.AttachmentTool:
      return `Attachment ID: ${toolCallWithProps.attachmentId || 'N/A'}`;
    case DeepResearchToolType.PublicDataSubAgent:
      return `Query: "${toolCallWithProps.query || ''}"`;
    case DeepResearchToolType.IdsToModelsTool:
      return `Object IDs: ${toolCallWithProps.objectIds?.join(', ')}\nInclude Relevant Items: ${toolCallWithProps.includeRelevantItems || false}`;
    case DeepResearchToolType.ResearchSubAgent:
    case DeepResearchToolType.Think:
    case DeepResearchToolType.CompleteAnswer:
    case DeepResearchToolType.GetWarehouseOverviewTool:
    default:
      // For tools without specific input parameters, show generic message
      return 'No input parameters';
  }
}

/**
 * Component for displaying detailed information about generic tool calls
 * (ResearchSubAgent, Think, CompleteAnswer, etc.).
 */
export function BaseToolDetail({toolCall}: BaseToolDetailProps): JSX.Element {
  const inputString = getToolInputString(toolCall);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.section)}>
        <div className={css(styles.sectionLabel)}>Input:</div>
        <div className={css(styles.codeBlock)}>{inputString}</div>
      </div>

      <div className={css(styles.section)}>
        <div className={css(styles.sectionLabel)}>Output:</div>
        <div className={css(styles.codeBlock)}>{toolCall.responseText || 'No response available'}</div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  sectionLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
  },

  codeBlock: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    padding: '12px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.4',
    maxHeight: '250px',
    overflowY: 'auto',
  },
});
